import { useState, useMemo } from 'react'
import { Users, Crown, Shield, Trash2, UserPlus, X, Eye, Lock } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { useChatMembers, useRemoveChatMember, useAddChatMember } from '@/hooks/useChat'
import { api } from '@/lib/api'
import { cn, gravatarUrl, debounce } from '@/lib/utils'
import type { Chat, ChatMembership, ChatRole, User } from '@/types/chat'

/**
 * ChatSettingsDialog - manage chat members and settings.
 *
 * Features:
 * - View all chat members with their roles
 * - Add new members (owner/moderator only)
 * - Remove members (owner/moderator only)
 * - Visual indicators for roles and permissions
 */

interface ChatSettingsDialogProps {
  chat: Chat
  isOpen: boolean
  onClose: () => void
  currentUsername: string
}

export function ChatSettingsDialog({
  chat,
  isOpen,
  onClose,
  currentUsername,
}: ChatSettingsDialogProps) {
  const { data: members, isLoading } = useChatMembers(chat.id)
  const removeMember = useRemoveChatMember()
  const addMember = useAddChatMember()

  const [isAddingMember, setIsAddingMember] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [searchResults, setSearchResults] = useState<User[]>([])
  const [isSearching, setIsSearching] = useState(false)

  // Check if current user can manage members
  const currentUserMembership = members?.find((m) => m.member.username === currentUsername)
  const canManageMembers =
    currentUserMembership?.role === 'OWNER' ||
    currentUserMembership?.role === 'MODERATOR' ||
    currentUserMembership?.role === 'DEPUTY'

  // Search for users to add (debounced)
  const searchUsers = useMemo(
    () =>
      debounce((term: string) => {
        if (!term.trim()) {
          setSearchResults([])
          return
        }

        setIsSearching(true)
        api
          .get<User[]>(`/api/search/users/${encodeURIComponent(term)}`)
          .then((results) => {
            // Filter out users already in the chat
            const existingUsernames = new Set(members?.map((m) => m.member.username) || [])
            setSearchResults(results.filter((u) => !existingUsernames.has(u.username)))
          })
          .catch(() => {
            setSearchResults([])
          })
          .finally(() => {
            setIsSearching(false)
          })
      }, 300),
    [members]
  )

  const handleSearchChange = (value: string) => {
    setSearchTerm(value)
    searchUsers(value)
  }

  const handleAddMember = async (user: User) => {
    try {
      await addMember.mutateAsync({ chatId: chat.id, userId: user.id })
      setSearchTerm('')
      setSearchResults([])
      setIsAddingMember(false)
    } catch {
      // Error handling done by mutation
    }
  }

  const handleRemoveMember = async (membership: ChatMembership) => {
    if (!confirm(`Remove ${membership.member.username} from this chat?`)) return

    try {
      await removeMember.mutateAsync({ chatId: chat.id, membershipId: membership.id })
    } catch {
      // Error handling done by mutation
    }
  }

  const getRoleIcon = (role: ChatRole) => {
    switch (role) {
      case 'OWNER':
        return (
          <span title="Owner">
            <Crown className="h-4 w-4 text-yellow-500" />
          </span>
        )
      case 'MODERATOR':
      case 'DEPUTY':
        return (
          <span title={role}>
            <Shield className="h-4 w-4 text-primary" />
          </span>
        )
      default:
        return null
    }
  }

  const getRoleLabel = (role: ChatRole) => {
    const labels: Record<ChatRole, string> = {
      OWNER: 'Owner',
      DEPUTY: 'Deputy',
      MODERATOR: 'Moderator',
      AUTHOR: 'Author',
      READER: 'Reader',
    }
    return labels[role] || role
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Chat Settings">
      <div className="space-y-6">
        {/* Chat Info */}
        <div className="rounded-lg bg-gray-50 p-4">
          <div className="flex items-center gap-3">
            <img
              src={gravatarUrl(chat.owner.gravatarHash, 48)}
              alt={chat.owner.username}
              className="h-12 w-12 rounded-full"
            />
            <div className="flex-1">
              <h3 className="font-semibold text-text-dark">
                {chat.chatName || chat.participants.map((p) => p.username).join(', ')}
              </h3>
              <div className="flex items-center gap-3 text-sm text-text-light">
                {chat.groupChat && (
                  <span className="flex items-center gap-1">
                    <Users className="h-3.5 w-3.5" />
                    Group
                  </span>
                )}
                {chat.readonly && (
                  <span className="flex items-center gap-1">
                    <Lock className="h-3.5 w-3.5" />
                    Read-only
                  </span>
                )}
                {chat.publicChat && (
                  <span className="flex items-center gap-1">
                    <Eye className="h-3.5 w-3.5" />
                    Public
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Members Section */}
        <div>
          <div className="mb-3 flex items-center justify-between">
            <h4 className="font-medium text-text-dark">
              Members {members && `(${members.length})`}
            </h4>
            {canManageMembers && !isAddingMember && (
              <button
                onClick={() => setIsAddingMember(true)}
                className="flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-sm text-white hover:bg-primary-dark"
              >
                <UserPlus className="h-4 w-4" />
                Add
              </button>
            )}
          </div>

          {/* Add Member Form */}
          {isAddingMember && (
            <div className="mb-4 rounded-lg border border-gray-200 bg-white p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-medium">Add member</span>
                <button
                  onClick={() => {
                    setIsAddingMember(false)
                    setSearchTerm('')
                    setSearchResults([])
                  }}
                  className="text-text-muted hover:text-text"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Search users..."
                className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                autoFocus
              />

              {/* Search Results */}
              {isSearching && (
                <div className="mt-2 flex items-center justify-center py-4">
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                </div>
              )}
              {!isSearching && searchResults.length > 0 && (
                <div className="mt-2 max-h-40 overflow-y-auto rounded-md border border-gray-100">
                  {searchResults.map((user) => (
                    <button
                      key={user.id}
                      onClick={() => handleAddMember(user)}
                      disabled={addMember.isPending}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 disabled:opacity-50"
                    >
                      <img
                        src={gravatarUrl(user.gravatarHash, 32)}
                        alt={user.username}
                        className="h-8 w-8 rounded-full"
                      />
                      <span className="text-sm">{user.username}</span>
                    </button>
                  ))}
                </div>
              )}
              {!isSearching && searchTerm && searchResults.length === 0 && (
                <p className="mt-2 text-center text-sm text-text-muted">No users found</p>
              )}
            </div>
          )}

          {/* Member List */}
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          ) : (
            <div className="space-y-2">
              {members?.map((membership) => (
                <div
                  key={membership.id}
                  className={cn(
                    'flex items-center gap-3 rounded-lg p-3',
                    membership.online ? 'bg-success-50' : 'bg-gray-50'
                  )}
                >
                  <div className="relative">
                    <img
                      src={gravatarUrl(membership.member.gravatarHash, 40)}
                      alt={membership.member.username}
                      className="h-10 w-10 rounded-full"
                    />
                    {membership.online && (
                      <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white bg-success" />
                    )}
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{membership.member.username}</span>
                      {getRoleIcon(membership.role)}
                    </div>
                    <span className="text-xs text-text-muted">{getRoleLabel(membership.role)}</span>
                  </div>

                  {/* Remove button (only for non-owners, by authorized users) */}
                  {canManageMembers &&
                    membership.role !== 'OWNER' &&
                    membership.member.username !== currentUsername && (
                      <button
                        onClick={() => handleRemoveMember(membership)}
                        disabled={removeMember.isPending}
                        className="rounded-md p-2 text-text-muted hover:bg-white hover:text-danger disabled:opacity-50"
                        title="Remove member"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Close Button */}
        <div className="flex justify-end border-t border-gray-200 pt-4">
          <button
            onClick={onClose}
            className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-text hover:bg-gray-200"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  )
}
